import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Clock3,
  ExternalLink,
  Search,
  ShoppingBasket,
  SlidersHorizontal,
  TrendingUp,
  X,
} from "lucide-react";
import {
  getDollarGeneralCouponCombos,
  isDollarGeneralCouponCombination,
  type DollarGeneralCouponCombination,
  type DollarGeneralCouponRule,
} from "../services/DollarGeneralCouponCombosService";
import { useStores } from "../hooks/useStores";
import { getRetailerProductUrl } from "../utils/productUrls";
import { resolveStoreDisplayLabel } from "../utils/storeDisplayLabel";
import { getProductImageUrl } from "../utils/imageUtils";

type SortMode = "savings" | "total" | "expiring";

const DG_COUPONS_URL = "https://www.dollargeneral.com/deals/coupons";

const money = (cents = 0) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

const expirationTime = (raw?: string) => {
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const formatExpiration = (raw?: string) => {
  if (!raw) return "See coupon for expiration";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return `Expires ${raw}`;
  return `Expires ${parsed.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

const titleFor = (rule: DollarGeneralCouponRule) => {
  const summary = rule.summary?.trim();
  const brand = rule.brand?.trim();
  if (summary && brand && !summary.toLowerCase().includes(brand.toLowerCase())) {
    return `${summary} on ${brand}`;
  }
  return summary || rule.description;
};

const compactComboLabel = (value: string) => {
  const trimmed = value.trim();
  const familiarTwoWordBrand = trimmed.match(/^(Air Wick|Betty Crocker|Heartland Farms|DG Health|Dollar General)/i)?.[0];
  if (familiarTwoWordBrand) return familiarTwoWordBrand;
  if (/^Head\s*&\s*Shoulders/i.test(trimmed)) return "Head & Shoulders";
  return trimmed.split(/\s+/)[0] || trimmed;
};

const bundleTitle = (combo: DollarGeneralCouponCombination) => {
  if (combo.kind === "same_item_stack") {
    return combo.lines[0]?.name || combo.title || "Same-item coupon stack";
  }
  if (combo.kind === "threshold_stack") {
    const threshold = combo.coupons.find(({ rule }) => rule.type === "spend_threshold")?.rule;
    if (threshold) {
      const brand = threshold.brand?.trim();
      const description = threshold.description.toLowerCase();
      const namedTarget = description.includes("gain")
        ? "Gain"
        : description.includes("mtn dew")
          ? "Mtn Dew"
          : description.includes("gatorade")
            ? "Gatorade"
            : description.includes("snuggle") && description.includes("purex")
              ? "Laundry"
              : undefined;
      const firstProduct = combo.lines[0]?.name ? compactComboLabel(combo.lines[0].name) : undefined;
      const targetName = brand && brand.toLowerCase() !== "dollar general" ? brand : namedTarget || firstProduct;
      const target = targetName ? ` ${targetName}` : "";
      return `${money(threshold.minimumSpendCents)}${target} basket`;
    }
  }
  const labels = combo.coupons.map(({ rule, lines }) => {
    const brand = rule.brand?.trim();
    if (brand && brand.toLowerCase() !== "dollar general") return compactComboLabel(brand);
    const productName = lines[0]?.name?.split(/\s[-–—]\s|,/)[0]?.trim();
    if (productName) return compactComboLabel(productName);
    return rule.summary?.trim() || "DG coupon";
  }).filter((label, index, all) => all.findIndex((value) => value.toLowerCase() === label.toLowerCase()) === index);
  return labels.slice(0, 4).join(" + ") || "Coupon basket";
};

const comboKindLabel = (combo: DollarGeneralCouponCombination) => {
  if (combo.kind === "same_item_stack") return "DG + manufacturer on the same item";
  if (combo.kind === "threshold_stack") {
    const productCoupons = Math.max(combo.couponCount - 1, 1);
    return `Basket coupon + ${productCoupons} product coupon${productCoupons === 1 ? "" : "s"}`;
  }
  return `${combo.couponCount} offers stack`;
};

const issuerLabel = (rule: DollarGeneralCouponRule) =>
  rule.issuer === "dg_store" ? "DG store coupon" : rule.issuer === "manufacturer" ? "Manufacturer coupon" : "Digital coupon";

const couponUrl = (rule: DollarGeneralCouponRule) => {
  const offerCode = rule.offerCode?.trim();
  if (!offerCode) return DG_COUPONS_URL;
  const couponType = rule.offerSourceType === 2 ? "earn-" : "save-";
  return `${DG_COUPONS_URL}/${couponType}/${encodeURIComponent(offerCode)}?vc=${rule.offerSourceType !== 2}`;
};

const availableBaskets = (combo: DollarGeneralCouponCombination) => {
  if (combo.lines.length === 0 || combo.lines.some((line) => !line.availableStock || line.quantity <= 0)) return undefined;
  return Math.min(...combo.lines.map((line) => Math.floor((line.availableStock || 0) / line.quantity)));
};

const basketStockLabel = (combo: DollarGeneralCouponCombination) => {
  const count = availableBaskets(combo);
  if (count === undefined) return "Stock varies";
  if (count <= 0) return "Not enough stock";
  return `${count} ${count === 1 ? "basket" : "baskets"} available`;
};

const basketRequirement = (rule: DollarGeneralCouponRule) => {
  const target = rule.brand && rule.brand.toLowerCase() !== "dollar general"
    ? `${rule.brand} products`
    : "eligible products";
  switch (rule.type) {
    case "spend_threshold":
      return `Spend ${money(rule.minimumSpendCents)} on ${target}`;
    case "n_for_price":
      return `Pick ${rule.requiredQuantity || 0} ${target} for ${money(rule.bundlePriceCents)}`;
    case "buy_get":
      return `Buy ${rule.buyQuantity || 0}, get ${rule.getQuantity || 0} ${rule.getDiscountPercent === 100 ? "free" : `${rule.getDiscountPercent || 0}% off`}`;
    case "percent_off_quantity":
      return `Buy ${rule.requiredQuantity || 1} eligible ${rule.brand || "coupon"} ${(rule.requiredQuantity || 1) === 1 ? "item" : "items"}, save ${rule.getDiscountPercent || 0}%`;
    case "amount_off_quantity":
      return `Buy ${rule.requiredQuantity || 1} eligible ${rule.brand || "coupon"} ${(rule.requiredQuantity || 1) === 1 ? "item" : "items"}`;
    default:
      return "See eligible basket";
  }
};

function ProductImages({ combo }: { combo: DollarGeneralCouponCombination }) {
  const image = combo.lines.find((line) => line.image)?.image;
  if (!image) {
    return (
      <div className="flex h-[70px] w-[70px] items-center justify-center rounded-xl bg-gray-50 text-primary-500 ring-1 ring-black/[0.04] dark:bg-dark-800 dark:text-primary-300 dark:ring-white/[0.06]">
        <ShoppingBasket className="h-6 w-6" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className="relative h-[70px] w-[70px] overflow-hidden rounded-xl bg-gray-50 ring-1 ring-black/[0.04] dark:bg-dark-800 dark:ring-white/[0.06]">
      <img src={image} alt="" className="h-full w-full object-contain" />
      {combo.couponCount > 1 && (
        <span className="absolute bottom-1 right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-[9px] font-black text-white shadow-sm ring-2 ring-white dark:ring-dark-800">
          +{combo.couponCount - 1}
        </span>
      )}
    </div>
  );
}

function ComboRow({
  combo,
  storeLabel,
  onOpen,
}: {
  combo: DollarGeneralCouponCombination;
  storeLabel: string;
  onOpen: () => void;
}) {
  const stockLabel = basketStockLabel(combo);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full overflow-hidden border-b border-gray-100 bg-white text-left transition-colors hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:border-dark-800 dark:bg-dark-900 dark:hover:bg-dark-800/40"
      aria-label={`View ${combo.couponCount}-coupon basket for ${bundleTitle(combo)}`}
    >
      <div className="px-3 py-[9px]">
        <div className="flex items-stretch gap-2.5">
          <div className="flex w-[72px] shrink-0 flex-col items-center gap-[3px]">
            <ProductImages combo={combo} />
            <span className="max-w-full truncate text-[8px] font-bold uppercase leading-[11px] tracking-[0.25px] text-primary-600 dark:text-primary-300">
              {combo.couponCount} coupons
            </span>
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 className="line-clamp-2 text-[13px] font-bold leading-[18px] tracking-[-0.1px] text-gray-900 dark:text-dark-50">
              {bundleTitle(combo)}
            </h2>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[10px] font-medium text-gray-500 dark:text-dark-300">
              <span className="truncate font-bold text-primary-600 dark:text-primary-300" title={storeLabel}>{storeLabel}</span>
              <span className="h-[3px] w-[3px] rounded-full bg-gray-300 dark:bg-dark-600" />
              <span className="shrink-0">{combo.totalItems} items</span>
              <span className="h-[3px] w-[3px] rounded-full bg-gray-300 dark:bg-dark-600" />
              <span className={`truncate ${stockLabel === "Not enough stock" ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>{stockLabel}</span>
            </div>
            <div className="mt-[4px] flex flex-wrap items-center gap-x-[5px] gap-y-1">
              <span className="text-[17px] font-extrabold leading-none tracking-[-0.3px] text-emerald-600 tabular-nums dark:text-emerald-400">
                {money(combo.finalTotalCents)}
              </span>
              <span className="text-[11px] text-gray-400 line-through tabular-nums dark:text-dark-400">
                {money(combo.subtotalCents)}
              </span>
              <span className="rounded-md bg-amber-100/80 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 tabular-nums dark:bg-amber-900/40 dark:text-amber-300">
                Save {money(combo.discountCents)}
              </span>
              <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                combo.savingsPercent > 70
                  ? "bg-orange-500/[0.12] text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                  : "bg-red-500/10 text-red-600 dark:bg-red-900/40 dark:text-red-300"
              }`}>
                {combo.savingsPercent}% off
              </span>
            </div>
            <div className="mt-auto flex items-center justify-end pt-1 text-[10px] text-gray-400 dark:text-dark-400">
              <Clock3 className="mr-1 h-3 w-3" aria-hidden="true" />
              <span className="truncate">{formatExpiration(combo.earliestExpirationDate)}</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function ComboSheet({
  combo,
  storeLabel,
  onClose,
}: {
  combo: DollarGeneralCouponCombination;
  storeLabel: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center sm:items-center sm:p-5" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-gray-950/45 backdrop-blur-[1px] sheet-scrim-in"
        onClick={onClose}
        aria-label="Close basket details"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="coupon-combo-title"
        className="relative max-h-[96dvh] w-full overflow-y-auto overscroll-contain rounded-t-3xl bg-white shadow-2xl dark:bg-dark-900 sm:max-h-[88vh] sm:max-w-xl sm:rounded-3xl sheet-panel-in-sm"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-5 py-4 dark:border-dark-700 dark:bg-dark-900">
          <div className="min-w-0">
            <div className="mb-1 text-xs font-extrabold tracking-wide text-primary-600 dark:text-primary-300">{storeLabel} · {comboKindLabel(combo)}</div>
            <h2 id="coupon-combo-title" className="text-lg font-black leading-tight text-gray-950 dark:text-white">
              {bundleTitle(combo)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-dark-200 dark:hover:bg-dark-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pt-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-gray-900 dark:text-white">
            <ShoppingBasket className="h-4 w-4 text-primary-600" aria-hidden="true" />
            Buy these {combo.totalItems} items
          </div>
          <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 dark:divide-dark-700 dark:border-dark-700">
            {combo.lines.map((line) => (
              <div key={line.upc} className="flex items-center gap-3 p-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-white dark:bg-dark-800">
                  {line.image ? (
                    <img src={getProductImageUrl(line.image, "400")} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-primary-50 text-primary-500 dark:bg-primary-950/40">
                      <ShoppingBasket className="h-5 w-5" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-sm font-bold leading-5 text-gray-900 dark:text-white">{line.name}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-dark-400">
                    {line.availableStock !== undefined
                      ? `${line.quantity} needed · ${line.availableStock} in stock · ${money(line.unitCents)} each`
                      : `${line.quantity} × ${money(line.unitCents)}`}
                  </div>
                </div>
                <div className="shrink-0 text-sm font-extrabold text-gray-900 dark:text-white">{money(line.totalCents)}</div>
                <a
                  href={getRetailerProductUrl({ retailer: "dollar_general", url: `/p/${line.upc}` })}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-dark-700"
                  aria-label={`Open ${line.name} at Dollar General`}
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            ))}
          </div>

          <div className="mb-3 mt-5 flex items-center gap-2 text-sm font-extrabold text-gray-900 dark:text-white">
            <Check className="h-4 w-4 text-primary-600" aria-hidden="true" />
            Clip these {combo.couponCount} coupons
          </div>
          <div className="space-y-2">
            {combo.coupons.map((coupon, couponIndex) => (
              <a
                key={coupon.rule.couponId || coupon.rule.offerId || couponIndex}
                href={couponUrl(coupon.rule)}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-2xl border border-gray-200 px-3 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-dark-700 dark:hover:border-primary-700 dark:hover:bg-primary-950/10"
                aria-label={`Open ${titleFor(coupon.rule)} coupon at Dollar General`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-wide text-primary-600 dark:text-primary-300">{issuerLabel(coupon.rule)}</div>
                    <div className="mt-0.5 text-sm font-extrabold leading-5 text-gray-950 dark:text-white">{titleFor(coupon.rule)}</div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-dark-300">{basketRequirement(coupon.rule)} · {formatExpiration(coupon.rule.expirationDate)}</div>
                  </div>
                  <div className="flex shrink-0 items-start gap-2">
                    <div className="text-right">
                      <div className="text-[10px] font-semibold uppercase text-gray-400">Save</div>
                      <div className="text-sm font-black text-emerald-600 dark:text-emerald-400">{money(coupon.discountCents)}</div>
                    </div>
                    <ExternalLink className="mt-1 h-4 w-4 text-gray-400 transition-colors group-hover:text-primary-600 dark:text-dark-400 dark:group-hover:text-primary-300" aria-hidden="true" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          <dl className="mt-4 space-y-2 rounded-2xl bg-gray-50 p-4 text-sm dark:bg-dark-800">
            <div className="flex justify-between text-gray-600 dark:text-dark-300"><dt>Shelf subtotal</dt><dd className="font-bold">{money(combo.subtotalCents)}</dd></div>
            <div className="flex justify-between text-emerald-700 dark:text-emerald-400"><dt>Coupon savings</dt><dd className="font-black">−{money(combo.discountCents)}</dd></div>
            <div className="flex items-end justify-between border-t border-gray-200 pt-3 dark:border-dark-600">
              <dt className="font-extrabold text-gray-950 dark:text-white">Estimated total</dt>
              <dd className="text-2xl font-black text-gray-950 dark:text-white">{money(combo.finalTotalCents)}</dd>
            </div>
          </dl>

          <div className="mt-4 flex items-start gap-2 rounded-2xl bg-yellow-50 p-3 text-xs font-semibold leading-5 text-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-100">
            <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{combo.kind === "same_item_stack" ? "The same-item stack uses one DG store coupon and one manufacturer coupon. On buy-one-get-one offers, the item coupon must attach to a paid item." : "The basket coupon is calculated from the eligible shelf subtotal before the product coupons. Clip every coupon before checkout and verify the register total."}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

const sortOptions: Array<{ mode: SortMode; label: string }> = [
  { mode: "savings", label: "Highest % off" },
  { mode: "total", label: "Low total" },
  { mode: "expiring", label: "Expiring" },
];

export default function DollarGeneralCouponCombosPage() {
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("savings");
  const [selectedCombo, setSelectedCombo] = useState<DollarGeneralCouponCombination | null>(null);
  const { stores: dollarGeneralStores } = useStores("dollar_general");
  const query = useQuery({
    queryKey: ["dollar-general-coupon-combos", "all"],
    queryFn: () => getDollarGeneralCouponCombos("all"),
    staleTime: 5 * 60 * 1000,
  });
  const snapshotCombinations = useMemo(
    () => query.data?.snapshot?.combinations || [],
    [query.data?.snapshot?.combinations],
  );
  const realCombinationCount = snapshotCombinations.filter(isDollarGeneralCouponCombination).length;
  const needsBackendRefresh = snapshotCombinations.length > 0 && realCombinationCount === 0;
  const storeLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const store of query.data?.stores || []) {
      labels.set(
        store.storeId,
        resolveStoreDisplayLabel("dollar_general", store.storeId, dollarGeneralStores) || `#${store.storeId}`,
      );
    }
    return labels;
  }, [dollarGeneralStores, query.data?.stores]);
  const storeLabelFor = (storeId: string) => storeLabels.get(storeId) || `#${storeId}`;
  const combinations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = snapshotCombinations.filter(isDollarGeneralCouponCombination).filter((combo) => {
      if (!needle) return true;
      return [storeLabels.get(combo.storeId) || `#${combo.storeId}`, ...combo.coupons.flatMap(({ rule }) => [rule.summary, rule.brand, rule.description]), ...combo.lines.map((line) => line.name)]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(needle));
    });
    return [...matches].sort((a, b) => {
      if (sortMode === "total") return a.finalTotalCents - b.finalTotalCents || b.savingsPercent - a.savingsPercent;
      if (sortMode === "expiring") return expirationTime(a.earliestExpirationDate) - expirationTime(b.earliestExpirationDate) || b.discountCents - a.discountCents;
      return b.savingsPercent - a.savingsPercent || b.discountCents - a.discountCents || a.finalTotalCents - b.finalTotalCents;
    });
  }, [snapshotCombinations, search, sortMode, storeLabels]);
  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-950 dark:to-dark-900">
      <header className="relative z-20 shrink-0 border-b border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900">
        <div className="flex h-11 items-center gap-1.5 border-b border-gray-100 px-2.5 dark:border-dark-800">
          <Link
            to="/dollar_general/products"
            aria-label="Back to Dollar General products"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-dark-300 dark:hover:bg-dark-800 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[15px] font-extrabold tracking-[-0.2px] text-gray-950 dark:text-white">Coupon Combos</h1>
          </div>
          {query.data?.snapshot && !needsBackendRefresh && (
            <span className="shrink-0 rounded-full bg-primary-50 px-2 py-1 text-[10px] font-bold text-primary-700 dark:bg-primary-950/40 dark:text-primary-300">
              {realCombinationCount} {realCombinationCount === 1 ? "combo" : "combos"}
            </span>
          )}
        </div>

        <div className="space-y-1.5 px-2.5 py-1.5">
          <label className="relative block">
            <span className="sr-only">Search coupons</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-gray-400 dark:text-dark-400" aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search coupon combos"
              className={`h-9 w-full rounded-xl border border-transparent bg-gray-100/80 pl-9 text-[13px] font-medium text-gray-900 outline-none transition-all placeholder:font-normal placeholder:text-gray-400 focus:border-primary-500/30 focus:bg-white focus:ring-2 focus:ring-primary-500/15 dark:bg-dark-800 dark:text-white dark:placeholder:text-dark-400 dark:focus:bg-dark-700 ${search ? "pr-9" : "pr-3"}`}
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-600 dark:text-dark-400 dark:hover:bg-dark-700 dark:hover:text-dark-200" aria-label="Clear search">
                <X className="h-[13px] w-[13px]" aria-hidden="true" />
              </button>
            )}
          </label>

          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            <label className="relative shrink-0">
              <span className="sr-only">Sort coupon combinations</span>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as SortMode)}
                className="h-8 appearance-none rounded-full border border-gray-200 bg-white py-0 pl-7 pr-7 text-xs font-semibold text-gray-700 outline-none transition-colors hover:border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500/20 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-200 dark:hover:border-dark-500 dark:hover:bg-dark-700"
              >
                {sortOptions.map(({ mode, label }) => <option key={mode} value={mode}>{label}</option>)}
              </select>
              <TrendingUp className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-500 dark:text-dark-400" aria-hidden="true" />
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400 dark:text-dark-400" aria-hidden="true" />
            </label>

            {query.data?.snapshot && (
              <span className="flex h-8 shrink-0 items-center rounded-full border border-gray-200 bg-white px-3 text-xs font-medium text-gray-500 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-400">
                {query.data.snapshot.couponsScanned} coupons scanned{query.data.snapshot.storeId === "all" ? " across stores" : ""}
              </span>
            )}
          </div>

          {needsBackendRefresh && (
            <p className="text-[10px] font-medium text-amber-700 dark:text-amber-300">Coupon baskets need a server refresh</p>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="w-full bg-white dark:bg-dark-900">

        {query.isLoading && (
          <div className="divide-y divide-gray-100 dark:divide-dark-800" aria-label="Loading coupon combinations">
            {[0, 1, 2, 3, 4].map((item) => (
              <div key={item} className="flex animate-pulse gap-2.5 px-3 py-[9px]">
                <div className="h-[70px] w-[70px] shrink-0 rounded-xl bg-gray-100 dark:bg-dark-800" />
                <div className="flex-1 space-y-2 pt-1"><div className="h-3.5 w-4/5 rounded bg-gray-100 dark:bg-dark-800" /><div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-dark-800" /><div className="h-4 w-2/3 rounded bg-gray-100 dark:bg-dark-800" /><div className="ml-auto h-2.5 w-1/3 rounded bg-gray-100 dark:bg-dark-800" /></div>
              </div>
            ))}
          </div>
        )}

        {query.isError && (
          <div className="m-4 rounded-2xl border border-red-200 bg-red-50 p-5 text-center dark:border-red-900/40 dark:bg-red-950/20">
            <SlidersHorizontal className="mx-auto h-6 w-6 text-red-500" aria-hidden="true" />
            <h2 className="mt-2 font-extrabold text-red-800 dark:text-red-200">Coupon combinations could not load</h2>
            <button type="button" onClick={() => query.refetch()} className="mt-3 rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">Try again</button>
          </div>
        )}

        {!query.isLoading && !query.isError && query.data?.snapshot && combinations.length > 0 && (
          <div>{combinations.map((combo) => <ComboRow key={`${combo.storeId}-${combo.id}`} combo={combo} storeLabel={storeLabelFor(combo.storeId)} onOpen={() => setSelectedCombo(combo)} />)}</div>
        )}

        {!query.isLoading && !query.isError && query.data?.snapshot && needsBackendRefresh && (
          <div className="px-6 py-16 text-center">
            <ShoppingBasket className="mx-auto h-8 w-8 text-primary-400" aria-hidden="true" />
            <h2 className="mt-3 font-extrabold text-gray-900 dark:text-white">Restart the admin server</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm leading-5 text-gray-500 dark:text-dark-400">
              The running server is still returning the older single-coupon format. Restart it, then reload this page to see the real baskets.
            </p>
            <button type="button" onClick={() => query.refetch()} className="mt-4 rounded-xl bg-primary-600 px-4 py-2 text-sm font-bold text-white hover:bg-primary-700">Try again</button>
          </div>
        )}

        {!query.isLoading && !query.isError && query.data?.snapshot && !needsBackendRefresh && combinations.length === 0 && (
          <div className="px-6 py-16 text-center">
            <Search className="mx-auto h-8 w-8 text-gray-300 dark:text-dark-500" aria-hidden="true" />
            <h2 className="mt-3 font-extrabold text-gray-900 dark:text-white">No matching coupon combos</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">Try a product, brand, or a different sort.</p>
          </div>
        )}

        {!query.isLoading && !query.isError && !query.data?.snapshot && (
          <div className="px-6 py-16 text-center">
            <ShoppingBasket className="mx-auto h-8 w-8 text-gray-300 dark:text-dark-500" aria-hidden="true" />
            <h2 className="mt-3 font-extrabold text-gray-900 dark:text-white">No coupon combos available</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">The latest scans did not produce an in-stock combination yet.</p>
          </div>
        )}
        </div>
      </div>
      {selectedCombo && <ComboSheet combo={selectedCombo} storeLabel={storeLabelFor(selectedCombo.storeId)} onClose={() => setSelectedCombo(null)} />}
    </main>
  );
}
